import React from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'
import './PWCTrailersPage.css'

interface PWCModel {
  model: string;
  capacity: number;
  seating: string;
  overallLength: string;
  gvwr: number;
  tongueJack: string;
  axles: number;
  brakes: string;
  tireSize: string;
  overallWidth: string;
  betweenFenders: string;
}

const PWCTrailersPage: React.FC = () => {
  // Aluminum PWC Trailers
  const aluminumModels: PWCModel[] = [
    {
      model: "VAW-1200",
      capacity: 1200,
      seating: "2/3 Seater",
      overallLength: "14'3\"",
      gvwr: 1380,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "4.80 x 12 LRC 5H",
      overallWidth: "60\"",
      betweenFenders: "42\""
    },
    {
      model: "VADW-2000",
      capacity: 2000,
      seating: "(2) 10'-12' units",
      overallLength: "17'2\"",
      gvwr: 2420,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "5.30 x 12 LRD 5H",
      overallWidth: "102\"",
      betweenFenders: "85\""
    },
    {
      model: "VADW-2600",
      capacity: 2600,
      seating: "(2) 12'-14' units",
      overallLength: "18'7\"",
      gvwr: 3160,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "20.5x8x10 LRF 5H",
      overallWidth: "102\"",
      betweenFenders: "85\""
    }
  ];

  // Galvanized PWC Trailers
  const galvanizedModels: PWCModel[] = [
    {
      model: "VW-1200",
      capacity: 1200,
      seating: "2/3 Seater",
      overallLength: "13'",
      gvwr: 1420,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "4.80 x 12 LRC 5H",
      overallWidth: "60\"",
      betweenFenders: "42\""
    },
    {
      model: "VW-2000",
      capacity: 2000,
      seating: "Double",
      overallLength: "18'",
      gvwr: 2420,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "20.5 X 8.0 X 10 LRD 5H",
      overallWidth: "102\"",
      betweenFenders: "84\""
    }
  ];

  const pwcTrailerFeatures = [
    "Plug & Play Lights",
    "Impact-resistant plastic fenders",
    "Heavy Duty Winch Straps",
    "D.O.T. Composite Brake Line with Brass Fittings",
    "Balanced Radial Tires",
    "Super Lube Spindles",
    "Galvanized Hardware, U-bolts, Winch Stand, Axles, Tongue",
    "Adjustable bunk systems for perfect fit",
    "Submersible LED lighting systems",
    "Drive-on/drive-off design for easy loading",
    "Padded bunks to protect PWC hull",
    "Easy-to-use tie-down points"
  ];

  return (
    <div className="pwc-page">
      <SEOHead
        title="PWC & Jet Ski Trailers - Personal Watercraft Transport | Fast Boat Trailers"
        description="PWC and jet ski trailers from Fast Boat Trailers in Edgewater, MD. Single and double PWC trailers for Sea-Doo, Yamaha WaveRunner, Kawasaki. Aluminum and galvanized options."
        keywords="PWC trailers, jet ski trailers, personal watercraft trailers, Sea-Doo trailers, WaveRunner trailers, double PWC trailer Maryland"
        ogTitle="PWC & Jet Ski Trailers - Personal Watercraft Transport"
        ogDescription="PWC and jet ski trailers for Sea-Doo, Yamaha WaveRunner, Kawasaki. Single and double watercraft options available."
        ogImage="/images/trailers/pwc.jpg"
        canonical="https://fastboattrailers.com/trailers/pwc"
      />
      <div className="container">
        {/* Hero Section */}
        <div className="pwc-hero">
          <div className="hero-main">
            <div className="hero-content">
              <h1>Personal Watercraft Trailers</h1>
              <h2>Single & Tandem Options for Maximum Fun</h2>
              <p>
                Maximize your time on the water with the perfect trailer from Venture Trailers. Our personal watercraft trailers (PWCs) make launching and loading as easy as possible. 
                Whether you have one or two jet skis to haul, our PWC trailers are customizable to your recreational needs. 
                Venture Trailer's personal watercraft trailers are available in aluminum and hot dipped galvanized finishes.
              </p>
              
              <h3>Benefits of PWC Trailers</h3>
              <ul className="benefits-list">
                <li>Single or tandem trailer options</li>
                <li>Easy load and launch design</li>
                <li>Impact-resistant plastic fenders</li>
                <li>Drive-on/drive-off convenience</li>
                <li>Perfect for jet skis and wave runners</li>
              </ul>
            </div>
            <div className="hero-features">
              <h3>Standard Features</h3>
              <div className="features-compact">
                {pwcTrailerFeatures.map((feature, index) => (
                  <div key={index} className="feature-item-compact">
                    <div className="feature-icon-compact">✓</div>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Aluminum PWC Trailers Section */}
        <div className="models-section">
          <h2>Aluminum Personal Watercraft Trailers</h2>
          
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/pwc-aluminum-double-axle.jpg" alt="Aluminum Personal Watercraft Trailers" />
            </div>
            <div className="category-header">
              <h3>Aluminum PWC Trailers</h3>
              <p>Lightweight aluminum construction provides excellent corrosion resistance and durability. Available in single and tandem configurations with capacity ranges from 1,200 to 2,600 lbs.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity (lbs)</th>
                    <th>Seating/Units</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Tongue Jack</th>
                    <th>Axles</th>
                    <th>Brakes</th>
                    <th>Tire Size</th>
                    <th>Overall Width</th>
                    <th>Between Fenders</th>
                  </tr>
                </thead>
                <tbody>
                  {aluminumModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()}</td>
                      <td>{model.seating}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.axles}</td>
                      <td>{model.brakes}</td>
                      <td>{model.tireSize}</td>
                      <td>{model.overallWidth}</td>
                      <td>{model.betweenFenders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Galvanized PWC Trailers Section */}
        <div className="models-section">
          <h2>Galvanized Personal Watercraft Trailers</h2>
          
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/pwc-galvanized-single-axle.jpg" alt="Galvanized Personal Watercraft Trailers" />
            </div>
            <div className="category-header">
              <h3>Galvanized PWC Trailers</h3>
              <p>Hot-dipped galvanized steel construction provides maximum corrosion protection for saltwater environments. Single and double configurations with capacity ranges from 1,200 to 2,000 lbs.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity (lbs)</th>
                    <th>Seating/Units</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Tongue Jack</th>
                    <th>Axles</th>
                    <th>Brakes</th>
                    <th>Tire Size</th>
                    <th>Overall Width</th>
                    <th>Between Fenders</th>
                  </tr>
                </thead>
                <tbody>
                  {galvanizedModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()}</td>
                      <td>{model.seating}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.axles}</td>
                      <td>{model.brakes}</td>
                      <td>{model.tireSize}</td>
                      <td>{model.overallWidth}</td>
                      <td>{model.betweenFenders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* PWC Types Section */}
        <div className="pwc-types-section">
          <h2>Perfect for All Personal Watercraft</h2>
          <div className="pwc-types-grid">
            <div className="pwc-type">
              <h3>Jet Skis</h3>
              <p>Yamaha, Kawasaki, and Honda jet skis up to 12 feet in length. Single and tandem trailer options available.</p>
            </div>
            <div className="pwc-type">
              <h3>Wave Runners</h3>
              <p>Yamaha WaveRunner series including VX, GP, FX, and VXR models. Adjustable bunks for perfect fit.</p>
            </div>
            <div className="pwc-type">
              <h3>Sea-Doo Watercraft</h3>
              <p>All Sea-Doo models including Spark, GTI, GTX, RXT, and Wake series. Drive-on design for easy loading.</p>
            </div>
            <div className="pwc-type">
              <h3>Other PWCs</h3>
              <p>Compatible with most personal watercraft brands and models up to 12 feet. Customizable configurations available.</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="pwc-cta">
          <h2>Ready to Hit the Water?</h2>
          <p>
            Get the perfect PWC trailer for your jet ski or wave runner. Our aluminum and galvanized options 
            provide the reliability and convenience you need for endless water adventures.
          </p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-primary">Get a quote</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWCTrailersPage;